import { doc, getDoc } from 'firebase/firestore';
import { useCallback, useMemo, useState } from 'react';
import { db } from '@/firebaseInit';
import type { HistorizedDocumentInHistoryType, HistoryStepType } from '@/types/types_history';
import { getStepIndex } from './util_history';

// Walks the archive chain written by the historized-document Cloud Function.
//
// The live document only carries the last PRESERVE_HISTORY_STEPS steps. Each
// archived copy in `{collection}history` carries the window of steps as it
// stood when that step was written — so any step we already know about names
// the archive document holding the batch that preceded it. Loading one walks a
// window further back, and so on until step 1.
//
// Adapted from the portal's src/hooks/useHistoryPanel.ts. The portal
// accumulates live steps in state so that a step ageing off the live document
// while the panel is open doesn't leave a hole; here the same case is handled
// by which step we choose to load next (below), which needs no state at all.
//
// This hook holds state for one document. Callers must key the component on the
// document id so it remounts rather than carrying another document's steps.

/**
 * The step whose archived copy would extend the log backwards: the lowest known
 * step whose immediate predecessor is missing. Normally that is simply the
 * oldest step we hold, but if the window shifted while the panel was open it is
 * the bottom of the gap — and loading that step's archive fills the gap in.
 * Undefined once step 1 is present, i.e. the whole log is loaded.
 */
function getNextHistoryStepToLoad(steps: Map<string, HistoryStepType>): string | undefined {
  const known = [...steps.keys()].map(getStepIndex).sort((a, b) => a - b);
  for (const stepNum of known) {
    if (stepNum === 1) continue; // the beginning of the log, nothing precedes it
    if (!steps.has(`step_${stepNum - 1}`)) return steps.get(`step_${stepNum}`)?.stepId;
  }
  return undefined; // contiguous back to step 1 (or no steps at all)
}

export function useHistoryPanel(
  historyCollectionName: string,
  liveDocHistorySteps: Record<string, HistoryStepType> | undefined,
) {
  const [loadedHistorySteps, setLoadedHistorySteps] = useState<Map<string, HistoryStepType>>(() => new Map());
  const [historyLoading, setHistoryLoading] = useState(false);

  const historySteps = useMemo(() => {
    const combined = new Map(loadedHistorySteps);
    for (const [key, step] of Object.entries(liveDocHistorySteps ?? {})) {
      combined.set(key, step);
    }
    return combined;
  }, [loadedHistorySteps, liveDocHistorySteps]);

  const nextStepToLoad = useMemo(() => getNextHistoryStepToLoad(historySteps), [historySteps]);

  const loadMoreHistory = useCallback(async () => {
    if (!nextStepToLoad) return;
    setHistoryLoading(true);
    try {
      const snap = await getDoc(doc(db, historyCollectionName, nextStepToLoad));
      if (!snap.exists()) {
        // Expected for documents last edited before archiving was switched on:
        // their older steps were trimmed with nowhere to go.
        console.warn(`No history doc ${historyCollectionName}/${nextStepToLoad}`);
        return;
      }
      const archived = snap.data() as HistorizedDocumentInHistoryType;
      const archivedHistory = archived.history;
      if (!archivedHistory) {
        console.warn(`History doc ${nextStepToLoad} has no history field`);
        return;
      }
      setLoadedHistorySteps((prev) => {
        const next = new Map(prev);
        for (const [key, step] of Object.entries(archivedHistory.steps)) {
          next.set(key, step);
        }
        return next;
      });
    } catch (error) {
      console.error('Loading older history failed', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyCollectionName, nextStepToLoad]);

  return { historySteps, nextStepToLoad, historyLoading, loadMoreHistory };
}
